/**
 * Update safety on a node (D40, PLAN §4.2; docs/runtime.md, "Update safety"): the files the supervisor, the janitor
 * and the state service hand each other while a release is installed, swapped in, proven, rolled back or refused,
 * and what the node tells the cloud of it.
 *
 * They are files, not new supervisor ops: the supervisor ships in the image and its control protocol v1 is frozen
 * (`@norna/supervisor-ipc/protocol.js` — a new op or field is a new version), while these three processes come from
 * different releases and read each other's files across a rollback. So every one is a storage boundary (D37 §4.6):
 * read tolerantly, each shape on `dev/compat/storage.ts`, a field only ever added.
 *
 *     <root>/supervisor/rejected.json          the supervisor: the state-service packages it rolled back from (D40)
 *     <root>/pkg/refused.json                  the janitor: the releases its install gate refused (§6 G)
 *     <root>/pkg/update-waiting.json           the janitor: a release installed but held back by the on-air guard
 *     <root>/pkg/previous.json                 the janitor: the release before the current one (a rollback's target)
 *     <root>/pkg/cleared.json                  the janitor: the vendor's clears of this node's rejections (the last manifest's)
 *     <run>/state-service.ready.json           the state service: it is up, and how its reattach went
 *     <root>/data/state-service/live-ipc.json  the state service: which IPC versions each live production offered
 *     <root>/data/state-service/release-reports.json   the state service: productions carried to their end, per release
 *     install/state-service.<hash>/acp-state-service.json   the package's own declaration (the release build writes it)
 */
import { z } from 'zod';
import { openEnum } from './openEnum.js';
import { isoTimestampWireSchema, sha256HexWireSchema } from './primitives.js';

/**
 * Why a node would not keep a release:
 *
 *   not-ready           the new state service did not say it was ready in time (~10 s) — the supervisor rolled back;
 *   reattach-failed     it came up but could not reattach a live production — rolled back;
 *   died-young          it exited during its trial — rolled back;
 *   ipc-version-in-use  the janitor's install gate: its state service drops every IPC version a live production here
 *                       speaks (docs/compatibility.md §4.1, §6 G) — never installed.
 *
 * Open: a newer supervisor or janitor may name another reason, and the cloud and the SPA read it.
 */
export const RELEASE_REJECTION_REASONS = ['not-ready', 'reattach-failed', 'died-young', 'ipc-version-in-use'] as const;
export const releaseRejectionReasonWireSchema = openEnum(RELEASE_REJECTION_REASONS);
export type ReleaseRejectionReasonWire = z.infer<typeof releaseRejectionReasonWireSchema>;

/** At most this many rejections are kept in a file (the newest): a node never needs more than the last few. */
export const MAX_KEPT_REJECTIONS = 20;

/** One release a node would not keep. `listHash` null: the supervisor could not tell which list the package came in. */
export const releaseRejectionWireSchema = z.object({
  listHash: sha256HexWireSchema.nullable(),
  /** The rejected state-service package (null for a refusal decided before any package ran). */
  stateService: sha256HexWireSchema.nullable(),
  reason: releaseRejectionReasonWireSchema,
  /** What happened, for a person (the log line). */
  detail: z.string().max(2000),
  at: isoTimestampWireSchema,
});
export type ReleaseRejectionWire = z.infer<typeof releaseRejectionWireSchema>;

/** `rejected.json` (the supervisor's) and `refused.json` (the janitor's): the same shape, one writer each. */
export const releaseRejectionsFileWireSchema = z.object({
  v: z.literal(1),
  rejections: z.array(releaseRejectionWireSchema).max(MAX_KEPT_REJECTIONS),
});
export type ReleaseRejectionsFileWire = z.infer<typeof releaseRejectionsFileWireSchema>;

/**
 * `cleared.json`: the node's rejections the vendor cleared, as the last manifest said (`clearedRejections`) — kept on
 * disk so a janitor that restarts offline does not read a cleared rejection as in force again and roll the node back.
 */
export const clearedRejectionsFileWireSchema = z.object({
  v: z.literal(1),
  cleared: z.array(z.object({ listHash: sha256HexWireSchema, clearedAt: isoTimestampWireSchema })).max(50),
});
export type ClearedRejectionsFileWire = z.infer<typeof clearedRejectionsFileWireSchema>;

/** `update-waiting.json`: the janitor installed `listHash` but holds the state-service swap back (the on-air guard, D40). Absent: nothing waits. */
export const updateWaitingFileWireSchema = z.object({
  v: z.literal(1),
  listHash: sha256HexWireSchema,
  commit: z.string().min(1).max(64),
  /** Productions alive on this server when the janitor last looked (active or standby: a pair holds every active one on both). */
  productionsActive: z.number().int().nonnegative(),
  /** When the janitor first held it back. */
  since: isoTimestampWireSchema,
});
export type UpdateWaitingFileWire = z.infer<typeof updateWaitingFileWireSchema>;

/**
 * `state-service.ready.json` in the run directory: written by a state service once it listens, has reattached to the
 * productions already running and has started its links (the supervisor's readiness signal for a new package — a
 * package that declares `readyFile`). `failed` names each live production it could not reattach.
 */
export const stateServiceReadyFileWireSchema = z.object({
  v: z.literal(1),
  pid: z.number().int().positive(),
  packageHash: z.string().min(1).max(128),
  readyAt: isoTimestampWireSchema,
  reattach: z.object({
    attached: z.number().int().nonnegative(),
    failed: z.array(z.object({ productionId: z.string().min(1).max(128), reason: z.string().max(500) })).max(200),
  }),
});
export type StateServiceReadyFileWire = z.infer<typeof stateServiceReadyFileWireSchema>;

/** The file name of the declaration at a state-service package's root. */
export const STATE_SERVICE_DECLARATION_FILE = 'acp-state-service.json';

/**
 * `acp-state-service.json` (the release build writes it; D37 §4.4, like the production's `acp-production.json`): the
 * IPC versions the package's state service speaks to productions, and whether it writes the ready file. A package
 * without one is from before D40: the install gate cannot judge it (it installs it), and the supervisor judges its
 * start by its process alone.
 */
export const stateServiceDeclarationWireSchema = z.object({
  declarationVersion: z.literal(1),
  ipcProtocols: z.array(z.number().int().positive()).min(1).max(16),
  /** It writes `state-service.ready.json` once ready (D40's rollback reads it). */
  readyFile: z.boolean(),
});
export type StateServiceDeclarationWire = z.infer<typeof stateServiceDeclarationWireSchema>;

/**
 * `live-ipc.json`: the IPC versions each production linked to this state service OFFERED in its `hello` (D37 phase D)
 * and the one negotiated — what the janitor's install gate measures (docs/compatibility.md §4.1: "measured from the
 * hellos, not counted"). Rewritten when a link attaches or ends.
 */
export const liveIpcFileWireSchema = z.object({
  v: z.literal(1),
  updatedAt: isoTimestampWireSchema,
  productions: z
    .array(
      z.object({
        productionId: z.string().min(1).max(128),
        protocols: z.array(z.number().int().positive()).min(1).max(16),
        version: z.number().int().nonnegative(),
      }),
    )
    .max(500),
});
export type LiveIpcFileWire = z.infer<typeof liveIpcFileWireSchema>;

/** At most this many completions are kept (the newest). */
export const MAX_KEPT_COMPLETIONS = 20;

/** One production carried to its end on this node while it ran `listHash` (D40: main proves a release this way). */
export const productionCompletionWireSchema = z.object({
  listHash: sha256HexWireSchema,
  productionId: z.string().min(1).max(128),
  at: isoTimestampWireSchema,
});
export type ProductionCompletionWire = z.infer<typeof productionCompletionWireSchema>;

/** `release-reports.json`: the state service's completions, re-sent to the cloud on every connect (it keeps them idempotently). */
export const releaseReportsFileWireSchema = z.object({
  v: z.literal(1),
  completed: z.array(productionCompletionWireSchema).max(MAX_KEPT_COMPLETIONS),
});
export type ReleaseReportsFileWire = z.infer<typeof releaseReportsFileWireSchema>;

/** The newest `max` entries of `entries` after `entry` is added (by `at`, ties in insertion order). */
export function keepNewest<T extends { readonly at: string }>(entries: readonly T[], entry: T, max: number): T[] {
  return [...entries, entry].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0)).slice(-max);
}
